import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { saveLocalState, loadLocalState, clearLocalState, patchLocalState, cacheRefData, loadRefData } from "@/lib/offlineStore";

export const MAX_ON_COURT = 5;
export const blankAdd = () => ({ pts: 0, tpm: 0, tpa: 0, ftm: 0, fta: 0, fgm: 0, fga: 0, reb: 0, ast: 0, stl: 0, blk: 0, pf: 0, tech: 0 });
const emptyAdd = (a) => !a || Object.values(a).every((v) => !v);
const isNetworkError = (err) => !navigator.onLine || err?.message === "Network Error" || !err?.response;
// Lightweight signature to detect if a game changed on the server while we were offline.
const gameSig = (g) => JSON.stringify({ h: g?.home_score || 0, a: g?.away_score || 0, n: (g?.stats || []).length, pog: g?.player_of_game || "" });
const bodySig = (b) => JSON.stringify({ h: b?.home_score || 0, a: b?.away_score || 0, n: (b?.stats || []).length, pog: b?.player_of_game || "" });

// All scorekeeper state + actions. Behavior is identical to the previous inline implementation.
export function useScorekeeper(gameId, onFinalized) {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pendingSync, setPendingSync] = useState(false);
  const [savedLocalAt, setSavedLocalAt] = useState(0);
  const [conflict, setConflict] = useState(null); // { server } when the game changed on the server while offline
  const pendingRef = useRef(null);
  const baselineSigRef = useRef(null);
  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  const [history, setHistory] = useState([]);
  const [adds, setAdds] = useState({});
  const [homePlayer, setHomePlayer] = useState("");
  const [awayPlayer, setAwayPlayer] = useState("");
  const [saving, setSaving] = useState(false);
  const [timeouts, setTimeouts] = useState({ home: 0, away: 0 });
  const [playerDelays, setPlayerDelays] = useState({});
  const [autoSave, setAutoSave] = useState(true);
  const [saveStatus, setSaveStatus] = useState("");
  const [onCourt, setOnCourt] = useState({ home: [], away: [] });
  const [finalized, setFinalized] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [week, setWeek] = useState("");
  const [pog, setPog] = useState("");
  const [flash, setFlash] = useState({ side: null, label: "", positive: true, ts: 0 });
  const [foulOut, setFoulOut] = useState(null);
  const [fouledOutIds, setFouledOutIds] = useState([]);
  const dragRef = useRef(null);
  const initRef = useRef(false);

  const load = useCallback(() => {
    Promise.all([api.get("/games"), api.get("/teams"), api.get("/players")]).then(([g, t, p]) => {
      setGames(g.data); setTeams(t.data); setPlayers(p.data); setLoaded(true);
      cacheRefData({ games: g.data, teams: t.data, players: p.data });
    }).catch(() => {
      // Offline (or server unreachable): fall back to the last cached reference data so the game still opens.
      const ref = loadRefData();
      if (ref) { setGames(ref.games || []); setTeams(ref.teams || []); setPlayers(ref.players || []); }
      setLoaded(true);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const game = games.find((g) => g.id === gameId);
  const teamOf = (id) => teams.find((t) => t.id === id);
  const rosterOf = (id) => players.filter((p) => p.team_id === id);
  const playerById = (pid) => players.find((p) => p.id === pid);
  const sideOfPid = (pid) => (playerById(pid)?.team_id === game?.home_team_id ? "home" : "away");
  // Team fouls are always the sum of every player's personal fouls on that side.
  const teamFoulsOf = (side) => Object.entries(adds).reduce((s, [pid, a]) => (sideOfPid(pid) === side ? s + (a.pf || 0) : s), 0);

  useEffect(() => {
    if (!game || initRef.current) return;
    initRef.current = true;
    const local = loadLocalState(gameId);
    if (local && local.gameId === gameId) {
      // Restore the exact in-progress state saved on this device (survives refresh / offline).
      setHome(local.home || 0);
      setAway(local.away || 0);
      setWeek(local.week != null ? String(local.week) : (game.week != null ? String(game.week) : ""));
      setAdds(local.adds || {});
      setOnCourt(local.onCourt && (local.onCourt.home || local.onCourt.away) ? local.onCourt : { home: [], away: [] });
      setTimeouts(local.timeouts || { home: 0, away: 0 });
      setPlayerDelays(local.playerDelays || {});
      setHomePlayer(local.homePlayer || "");
      setAwayPlayer(local.awayPlayer || "");
      setFouledOutIds(local.fouledOutIds || []);
      setHistory(local.history || []);
      setPog(local.pog || "");
      pendingRef.current = local.pending || null;
      if (local.pending) setPendingSync(true);
      if (local.finalized) setFinalized(true);
      baselineSigRef.current = local.serverSig || gameSig(game);
      return;
    }
    setHome(game.home_score || 0);
    setAway(game.away_score || 0);
    setWeek(game.week != null ? String(game.week) : "");
    setTimeouts({ home: game.home_timeouts || 0, away: game.away_timeouts || 0 });
    if (Array.isArray(game.stats) && game.stats.length) {
      const restored = {};
      game.stats.forEach((s) => { restored[s.player_id] = { ...blankAdd(), ...s }; });
      setAdds(restored);
    }
    setOnCourt({
      home: rosterOf(game.home_team_id).filter((p) => p.eligible !== false).slice(0, MAX_ON_COURT).map((p) => p.id),
      away: rosterOf(game.away_team_id).filter((p) => p.eligible !== false).slice(0, MAX_ON_COURT).map((p) => p.id),
    });
    baselineSigRef.current = gameSig(game);
  }, [game, players]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrop = (side, target) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || d.side !== side) return;
    const list = onCourt[side];
    const alreadyOn = list.includes(d.pid);
    const draggedIneligible = playerById(d.pid)?.eligible === false;
    const addingToCourt = !alreadyOn && (target.type === "court" || target.type === "court-player");
    if (addingToCourt && fouledOutIds.includes(d.pid)) {
      toast.error(`${playerById(d.pid)?.name || "This player"} has fouled out and can't return to the court`);
      return;
    }
    if (addingToCourt && draggedIneligible) {
      const dp = playerById(d.pid);
      toast.error(`${dp?.name || "This player"} can't be added to the lineup — ${(dp?.eligibility_reasons || ["waiver/fee incomplete"]).join(", ")}`);
      return;
    }
    if (target.type === "court" && !alreadyOn && list.length >= MAX_ON_COURT) {
      toast.error(`Max ${MAX_ON_COURT} players on court`);
      return;
    }
    setOnCourt((prev) => {
      let l = [...prev[side]];
      if (target.type === "court") {
        if (!l.includes(d.pid)) l.push(d.pid);
      } else if (target.type === "bench") {
        l = l.filter((x) => x !== d.pid);
      } else if (target.type === "court-player") {
        if (target.pid === d.pid) return prev;
        if (l.includes(d.pid)) {
          const a = l.indexOf(d.pid), b = l.indexOf(target.pid);
          if (a >= 0 && b >= 0) { [l[a], l[b]] = [l[b], l[a]]; }
        } else {
          const idx = l.indexOf(target.pid);
          if (idx >= 0) l[idx] = d.pid;
        }
      }
      return { ...prev, [side]: l };
    });
  };

  const bump = (setter, side, delta) => setter((prev) => ({ ...prev, [side]: Math.max(0, prev[side] + delta) }));

  const mutateAdd = (pid, fn) => setAdds((prev) => {
    const c = { ...blankAdd(), ...(prev[pid] || {}) };
    fn(c);
    const copy = { ...prev };
    if (emptyAdd(c)) delete copy[pid]; else copy[pid] = c;
    return copy;
  });

  const flashNow = (side, label, positive = true) => setFlash({ side, label, positive, ts: Date.now() });

  const recordShot = (side, type, made) => {
    const pid = side === "home" ? homePlayer : awayPlayer;
    if (!pid) { toast.error("Select a player first"); return; }
    const pts = made ? (type === "ft" ? 1 : type === "2pt" ? 2 : 3) : 0;
    mutateAdd(pid, (c) => {
      if (type === "ft") { c.fta += 1; if (made) { c.ftm += 1; c.pts += 1; } }
      else if (type === "2pt") { c.fga += 1; if (made) { c.fgm += 1; c.pts += 2; } }
      else { c.fga += 1; c.tpa += 1; if (made) { c.fgm += 1; c.tpm += 1; c.pts += 3; } }
    });
    if (made) { if (side === "home") setHome((v) => v + pts); else setAway((v) => v + pts); }
    setHistory((h) => [...h, { kind: "shot", side, pid, type, made, pts }]);
    const lbl = made ? `+${pts}` : `${type.toUpperCase()} MISS`;
    flashNow(side, lbl, made);
  };

  const recordStat = (side, stat) => {
    const pid = side === "home" ? homePlayer : awayPlayer;
    if (!pid) { toast.error("Select a player first"); return; }
    mutateAdd(pid, (c) => { c[stat] += 1; });
    setHistory((h) => [...h, { kind: "stat", side, pid, stat }]);
    flashNow(side, `+1 ${stat.toUpperCase()}`, true);
  };

  const FOUL_OUT_LIMIT = 5;
  const EJECT_TECH_LIMIT = 2;
  const recordFoul = (side) => {
    const pid = side === "home" ? homePlayer : awayPlayer;
    if (!pid) { toast.error("Select a player first"); return; }
    const newPf = (adds[pid]?.pf || 0) + 1;
    mutateAdd(pid, (c) => { c.pf += 1; });
    setHistory((h) => [...h, { kind: "foul", side, pid }]);
    flashNow(side, "FOUL", false);
    if (newPf >= FOUL_OUT_LIMIT) {
      const p = playerById(pid);
      const nm = p ? `${p.number != null ? `#${p.number} ` : ""}${p.name}` : "Player";
      setFouledOutIds((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
      setOnCourt((prev) => ({ ...prev, [side]: prev[side].filter((x) => x !== pid) }));
      setFoulOut({ name: nm, count: newPf, side, ts: Date.now(), type: "foul" });
      if (side === "home") setHomePlayer((cur) => (cur === pid ? "" : cur));
      else setAwayPlayer((cur) => (cur === pid ? "" : cur));
    }
  };

  const recordTech = (side) => {
    const pid = side === "home" ? homePlayer : awayPlayer;
    if (!pid) { toast.error("Select a player first"); return; }
    const newTech = (adds[pid]?.tech || 0) + 1;
    mutateAdd(pid, (c) => { c.tech += 1; });
    setHistory((h) => [...h, { kind: "tech", side, pid }]);
    flashNow(side, "TECH", false);
    if (newTech >= EJECT_TECH_LIMIT) {
      const p = playerById(pid);
      const nm = p ? `${p.number != null ? `#${p.number} ` : ""}${p.name}` : "Player";
      setFouledOutIds((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
      setOnCourt((prev) => ({ ...prev, [side]: prev[side].filter((x) => x !== pid) }));
      setFoulOut({ name: nm, count: newTech, side, ts: Date.now(), type: "tech" });
      if (side === "home") setHomePlayer((cur) => (cur === pid ? "" : cur));
      else setAwayPlayer((cur) => (cur === pid ? "" : cur));
    }
  };

  const addPlayerDelay = (side) => {
    const pid = side === "home" ? homePlayer : awayPlayer;
    if (!pid) { toast.error("Select a player first"); return; }
    setPlayerDelays((prev) => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
  };
  const decPlayerDelay = (pid) => setPlayerDelays((prev) => {
    const n = (prev[pid] || 0) - 1; const copy = { ...prev };
    if (n <= 0) delete copy[pid]; else copy[pid] = n;
    return copy;
  });

  const subtractPoint = (side) => {
    const cur = side === "home" ? home : away;
    if (cur <= 0) return;
    if (side === "home") setHome((v) => Math.max(0, v - 1)); else setAway((v) => Math.max(0, v - 1));
    setHistory((h) => [...h, { kind: "adjust", side, pts: -1 }]);
    flashNow(side, "-1", false);
  };

  useEffect(() => {
    if (!flash.side) return;
    const t = setTimeout(() => setFlash({ side: null, label: "", positive: true, ts: 0 }), 700);
    return () => clearTimeout(t);
  }, [flash.ts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!foulOut) return;
    const t = setTimeout(() => setFoulOut(null), 6000);
    return () => clearTimeout(t);
  }, [foulOut]);

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    if (last.kind === "shot") {
      mutateAdd(last.pid, (c) => {
        if (last.type === "ft") { c.fta -= 1; if (last.made) { c.ftm -= 1; c.pts -= 1; } }
        else if (last.type === "2pt") { c.fga -= 1; if (last.made) { c.fgm -= 1; c.pts -= 2; } }
        else { c.fga -= 1; c.tpa -= 1; if (last.made) { c.fgm -= 1; c.tpm -= 1; c.pts -= 3; } }
      });
      if (last.made) { if (last.side === "home") setHome((v) => Math.max(0, v - last.pts)); else setAway((v) => Math.max(0, v - last.pts)); }
    } else if (last.kind === "stat") {
      mutateAdd(last.pid, (c) => { c[last.stat] -= 1; });
    } else if (last.kind === "foul") {
      mutateAdd(last.pid, (c) => { c.pf -= 1; });
      if ((adds[last.pid]?.pf || 0) - 1 < FOUL_OUT_LIMIT && (adds[last.pid]?.tech || 0) < EJECT_TECH_LIMIT) {
        setFouledOutIds((prev) => prev.filter((x) => x !== last.pid));
        if (foulOut && foulOut.side === last.side && foulOut.type === "foul") setFoulOut(null);
      }
    } else if (last.kind === "tech") {
      mutateAdd(last.pid, (c) => { c.tech -= 1; });
      if ((adds[last.pid]?.tech || 0) - 1 < EJECT_TECH_LIMIT && (adds[last.pid]?.pf || 0) < FOUL_OUT_LIMIT) {
        setFouledOutIds((prev) => prev.filter((x) => x !== last.pid));
        if (foulOut && foulOut.side === last.side && foulOut.type === "tech") setFoulOut(null);
      }
    } else if (last.kind === "adjust") {
      if (last.side === "home") setHome((v) => v + 1); else setAway((v) => v + 1);
    }
  };

  const buildStats = () => Object.entries(adds).map(([pid, a]) => {
    const p = playerById(pid);
    return {
      player_id: pid, team_id: p?.team_id || "", side: sideOfPid(pid),
      name: p?.name || "", number: p?.number ?? null,
      pts: a.pts || 0, tpm: a.tpm || 0, tpa: a.tpa || 0, ftm: a.ftm || 0, fta: a.fta || 0,
      fgm: a.fgm || 0, fga: a.fga || 0, reb: a.reb || 0, ast: a.ast || 0,
      stl: a.stl || 0, blk: a.blk || 0, pf: a.pf || 0, tech: a.tech || 0,
    };
  });

  const payload = (finish) => ({
    home_score: home, away_score: away,
    week: week === "" ? null : parseInt(week, 10),
    stats: buildStats(),
    player_of_game: finish ? pog : "",
    home_fouls: teamFoulsOf("home"), away_fouls: teamFoulsOf("away"),
    home_timeouts: timeouts.home, away_timeouts: timeouts.away,
  });

  // Persist the full in-progress state to this device on every change (survives refresh / close / offline).
  const persist = useCallback((extra = {}) => {
    if (!gameId) return;
    saveLocalState(gameId, {
      v: 1, gameId, home, away, week, adds, onCourt, timeouts, playerDelays,
      homePlayer, awayPlayer, fouledOutIds, history, pog,
      pending: pendingRef.current, finalized, serverSig: baselineSigRef.current,
      ...extra,
    });
    setSavedLocalAt(Date.now());
  }, [gameId, home, away, week, adds, onCourt, timeouts, playerDelays, homePlayer, awayPlayer, fouledOutIds, history, pog, finalized]);

  useEffect(() => {
    if (!game || !initRef.current || finalized) return;
    persist();
  }, [persist, game, finalized]);

  // Replay the latest queued action (stats save or finalize) once connectivity returns.
  // Before overwriting, check whether the game changed on the server while we were offline.
  const syncPending = useCallback(async (force = false) => {
    const local = loadLocalState(gameId);
    if (!local || !local.pending || !navigator.onLine) return;
    if (!force && baselineSigRef.current) {
      try {
        const fresh = await api.get("/games");
        const sg = fresh.data.find((x) => x.id === gameId);
        if (sg && gameSig(sg) !== baselineSigRef.current) {
          setConflict({ server: sg }); // let the user decide before overwriting
          return;
        }
      } catch { /* if we can't verify, fall through and attempt the sync */ }
    }
    try {
      if (local.pending.type === "finalize") {
        await api.post(`/games/${gameId}/finalize`, local.pending.body);
        clearLocalState(gameId);
      } else {
        await api.put(`/games/${gameId}/stats`, local.pending.body);
        baselineSigRef.current = bodySig(local.pending.body);
        pendingRef.current = null;
        patchLocalState(gameId, { pending: null, serverSig: baselineSigRef.current });
      }
      setPendingSync(false); setConflict(null); setSaveStatus("saved");
      toast.success("Synced with server ✓");
    } catch { /* stay pending; will retry on next online event */ }
  }, [gameId]);

  // Conflict resolvers.
  const forceSync = useCallback(() => { setConflict(null); syncPending(true); }, [syncPending]);
  const discardLocal = useCallback(() => {
    clearLocalState(gameId); pendingRef.current = null; setPendingSync(false); setConflict(null);
    window.location.reload();
  }, [gameId]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncPending(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [syncPending]);

  const saveProgress = async (silent = false) => {
    if (!game) { if (!silent) toast.error("Game not loaded"); return; }
    const body = payload(false);
    pendingRef.current = { type: "stats", body }; // queue locally first
    persist();
    setSaving(true);
    if (silent) setSaveStatus("saving");
    if (!navigator.onLine) {
      setPendingSync(true); setSaving(false); setSaveStatus("offline");
      if (!silent) toast.success("Saved on device — will sync when back online");
      return;
    }
    try {
      await api.put(`/games/${game.id}/stats`, body);
      pendingRef.current = null; baselineSigRef.current = bodySig(body);
      patchLocalState(gameId, { pending: null, serverSig: baselineSigRef.current });
      setPendingSync(false);
      if (silent) setSaveStatus("saved"); else toast.success("Progress saved");
    } catch (err) {
      if (isNetworkError(err)) {
        setPendingSync(true); setSaveStatus("offline");
        if (!silent) toast.success("Saved on device — will sync when back online");
      } else {
        setSaveStatus(""); if (!silent) toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally { setSaving(false); }
  };

  const confirmFinalize = async () => {
    if (!game) return;
    const body = payload(true);
    pendingRef.current = { type: "finalize", body };
    persist({ finalized: true });
    setSaving(true);
    if (!navigator.onLine) {
      setPendingSync(true); setFinalized(true); setFinalizing(false);
      toast.success("Game saved on device — it will finalize automatically when back online");
      onFinalized && onFinalized();
      setSaving(false);
      return;
    }
    try {
      await api.post(`/games/${game.id}/finalize`, body);
      clearLocalState(gameId); pendingRef.current = null; setPendingSync(false);
      toast.success("Game finalized — stats locked & standings updated");
      setFinalized(true); setFinalizing(false); onFinalized && onFinalized();
    } catch (err) {
      if (isNetworkError(err)) {
        setPendingSync(true); setFinalized(true); setFinalizing(false);
        toast.success("Saved on device — will finalize when back online");
        onFinalized && onFinalized();
      } else { toast.error(formatApiError(err.response?.data?.detail)); }
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (!autoSave || !game || finalized) return;
    const dirty = history.length > 0 || home !== (game.home_score || 0) || away !== (game.away_score || 0);
    if (!dirty) return;
    const t = setTimeout(() => saveProgress(true), 5000);
    return () => clearTimeout(t);
  }, [home, away, autoSave, game, history.length, finalized]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    game, loaded, home, away, history, adds, homePlayer, awayPlayer, setHomePlayer, setAwayPlayer,
    saving, timeouts, playerDelays, autoSave, setAutoSave, saveStatus, onCourt, finalized, finalizing,
    setFinalizing, week, setWeek, pog, setPog, flash, dragRef, setTimeouts,
    foulOut, setFoulOut, fouledOutIds,
    online, pendingSync, savedLocalAt, syncPending, conflict, forceSync, discardLocal,
    teamOf, rosterOf, playerById, teamFoulsOf,
    handleDrop, bump, recordShot, recordStat, recordFoul, recordTech, addPlayerDelay, decPlayerDelay,
    subtractPoint, undo, saveProgress, confirmFinalize,
  };
}
